import * as React from 'react';

import { VoiceboxChatIncoming } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatIncoming';
import { VoiceboxChatReasoning } from 'bones/components/molecules/Voicebox/chat/VoiceboxChatReasoning';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useReasoningStepsFromMessage } from 'bones/components/molecules/Voicebox/hooks/useReasoningStepsFromMessage';
import {
  VoiceboxResponseActionType,
  VoiceboxResponseMessage,
} from 'bones/constants/voicebox/message';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';

export interface VoiceboxChatMessageLoadingProps {
  isReasoningOpen: boolean;
  lastResponseMessage: VoiceboxResponseMessage | undefined;
  setIsReasoningOpen: (isOpen: boolean) => void;
  settings: VoiceboxSettings;
}

export const VoiceboxChatMessageLoading: React.VFC<
  VoiceboxChatMessageLoadingProps
> = ({
  isReasoningOpen,
  lastResponseMessage,
  setIsReasoningOpen,
  settings,
}) => {
  const isMounted = useIsMounted();
  const { copy, testIds } = useVoiceboxSchema();

  const [message, setMessage] = React.useState('');

  const updateMessage = React.useCallback(
    (messages: string[]) => {
      if (!isMounted.current) {
        return;
      }

      const nextMessage = messages.shift();
      //  istanbul ignore else
      if (nextMessage) {
        setMessage(nextMessage);
        setTimeout(() => {
          updateMessage(messages);
        }, copy.pending.timeout * 1000);
      }
    },
    [copy, isMounted]
  );

  React.useEffect(() => {
    updateMessage([...copy.pending.messages]);
  }, [copy, updateMessage]);

  const reasoningAction = lastResponseMessage?.actions?.find(
    (action) => action.type === VoiceboxResponseActionType.REASONING
  );
  const reasoningSteps = useReasoningStepsFromMessage({
    action: reasoningAction,
    message: lastResponseMessage,
  });

  return (
    <VoiceboxChatIncoming
      content={reasoningSteps.length ? null : `${message}...`}
      loading
      showActions={false}
      slot={
        lastResponseMessage && reasoningSteps.length ? (
          <VoiceboxChatReasoning
            isOpen={isReasoningOpen}
            message={lastResponseMessage}
            reasoningSteps={reasoningSteps}
            setIsOpen={setIsReasoningOpen}
            settings={settings}
          />
        ) : undefined
      }
      testId={testIds.messages.loading}
    />
  );
};
